import { useMutation } from '@apollo/client';
import type { Topic, UpsertTopicInput } from '@communities/content-management/domain';
import { UPSERT_COMMUNITY_GROUP_TOPIC, GET_COMMUNITY_GROUP_TOPICS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useState, useCallback } from 'react';

interface UpsertCommunityGroupTopicResponse {
  upsertCommunityGroupTopic: Topic;
}

export function useUpsertCommunityGroupTopic(communityGroupId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [upsertTopicMutation, { loading }] = useMutation<UpsertCommunityGroupTopicResponse>(UPSERT_COMMUNITY_GROUP_TOPIC, {
    refetchQueries: communityGroupId ? [{ query: GET_COMMUNITY_GROUP_TOPICS, variables: { communityGroupId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const upsertTopic = async (communityGroupId: string, topicName: string, input: UpsertTopicInput): Promise<boolean> => {
    try {
      track('UpsertCommunityGroupTopicInitiated', {
        communityGroupId,
        topicName,
      });

      await upsertTopicMutation({
        variables: {
          communityGroupId,
          topicName,
          input,
        },
      });

      track('UpsertCommunityGroupTopicSucceeded', {
        communityGroupId,
        topicName,
      });

      return true;
    } catch (error) {
      track('UpsertCommunityGroupTopicFailed', {
        communityGroupId,
        topicName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    upsertTopic,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpsertCommunityGroupTopic;
